import React, { useState } from 'react';
import { useQuery } from 'react-query';
import { Link } from 'react-router-dom';
import api from '../../services/api';
import { format } from 'date-fns';
import { ClipboardList, Download, Info, AlertTriangle, XCircle, ShieldAlert } from 'lucide-react';

// Gravedades, con el mismo vocabulario que el registro de eventos de 3CX
export const SEVERITY = {
  info:     { label: 'Información', icon: Info,        cls: 'text-blue-400' },
  warning:  { label: 'Advertencia', icon: Info,        cls: 'text-amber-500' },
  error:    { label: 'Error',       icon: XCircle,     cls: 'text-red-500' },
  critical: { label: 'Crítico',     icon: ShieldAlert, cls: 'text-red-600' },
};

function SeverityCell({ severity }) {
  const s = SEVERITY[severity] || SEVERITY.info;
  const Icon = s.icon;
  return (
    <span className="flex items-center gap-2 whitespace-nowrap">
      <Icon className={`w-4 h-4 ${s.cls}`} />
      <span>{s.label}</span>
    </span>
  );
}

/**
 * Registro de eventos.
 *   compact = true  → tarjeta para el dashboard (sin filtros, pocas filas)
 *   compact = false → tabla completa con filtros y paginación
 */
export default function EventLogPanel({ compact = false, limit = compact ? 8 : 25, filters = {} }) {
  const [offset, setOffset] = useState(0);
  const [rows, setRows] = useState([]);

  const params = new URLSearchParams({
    limit, offset,
    ...Object.fromEntries(Object.entries(filters).filter(([, v]) => v)),
  });

  const { data, isLoading } = useQuery(
    ['events', limit, offset, filters],
    () => api.get('/events?' + params).then(r => r.data),
    {
      keepPreviousData: true,
      refetchInterval: 30000,          // la bitácora se refresca sola
      onSuccess: (d) => {
        // offset 0 = primera carga o cambio de filtro: se reemplaza.
        setRows(prev => (offset === 0 ? d.events : [...prev, ...d.events]));
      },
    }
  );

  // Al cambiar los filtros se vuelve al principio
  React.useEffect(() => { setOffset(0); }, [JSON.stringify(filters)]);

  const download = () => {
    const q = new URLSearchParams(
      Object.fromEntries(Object.entries(filters).filter(([, v]) => v))
    );
    window.open(`/api/events/export?${q}`, '_blank');
  };

  return (
    <div className="card">
      <div className="flex items-center justify-between p-4 border-b border-gray-700">
        <h3 className="flex items-center gap-2 font-semibold">
          <ClipboardList className="w-5 h-5" />
          Registro de eventos
        </h3>
        {compact ? (
          <Link to="/events" className="text-sm text-primary-400 hover:underline">Ver todo</Link>
        ) : (
          <button onClick={download} className="btn-secondary flex items-center gap-2 text-sm">
            <Download className="w-4 h-4" /> Descargar
          </button>
        )}
      </div>

      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-left text-gray-400 border-b border-gray-700">
              <th className="px-4 py-3 font-medium">Gravedad</th>
              <th className="px-4 py-3 font-medium">ID del evento</th>
              <th className="px-4 py-3 font-medium">Fecha</th>
              {!compact && <th className="px-4 py-3 font-medium">Fuente</th>}
              {!compact && <th className="px-4 py-3 font-medium">Departamento</th>}
              <th className="px-4 py-3 font-medium">Detalles</th>
            </tr>
          </thead>
          <tbody>
            {rows.map(e => (
              <tr key={e.id} className="border-b border-gray-800 hover:bg-gray-800/40">
                <td className="px-4 py-3"><SeverityCell severity={e.severity} /></td>
                <td className="px-4 py-3">
                  <span className="text-primary-400">{e.event_id}</span>
                  {e.occurrences > 1 && (
                    <span className="ml-2 badge-warning text-xs" title="Veces que se ha repetido">
                      ×{e.occurrences}
                    </span>
                  )}
                </td>
                <td className="px-4 py-3 whitespace-nowrap">
                  {format(new Date(e.last_seen_at), 'dd/MM/yyyy H:mm')}
                </td>
                {!compact && <td className="px-4 py-3 whitespace-nowrap">{e.source}</td>}
                {!compact && <td className="px-4 py-3 whitespace-nowrap">{e.department || '—'}</td>}
                <td className="px-4 py-3">{e.details}</td>
              </tr>
            ))}
            {!rows.length && !isLoading && (
              <tr>
                <td colSpan={compact ? 4 : 6} className="px-4 py-8 text-center text-muted">
                  Sin eventos registrados. Buena señal.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {!compact && data?.hasMore && (
        <button
          onClick={() => setOffset(offset + limit)}
          className="w-full py-3 text-primary-400 hover:bg-gray-800/40 border-t border-gray-700"
        >
          Cargar Más
        </button>
      )}
    </div>
  );
}
